import {
  DESIGN_SUBCATEGORIES1,
  DESIGN_SUBCATEGORIES2,
} from "@/consts/category";
import { PrivacyTermsDialog } from "@/features/authentication/components/privacy-terms-dialog";
import { PublicLayout } from "@/features/navigation/components/public-layout.component";
import { prisma } from "@/services/db.server";
import { getCountriesCodeAndName } from "@/services/get-countries-code-and-name.server";
import { LegalTemplateRenderer } from "@/services/legal-template-renderer";
import { createS3UploadHandler } from "@/services/s3/s3-upload-handler.server";
import {
  Intent,
  getFormProps,
  getInputProps,
  getSelectProps,
  getTextareaProps,
  useForm,
  useInputControl,
} from "@conform-to/react";
import { conformZodMessage, parseWithZod } from "@conform-to/zod";
import {
  Button,
  Checkbox,
  FileInput,
  Radio,
  Select,
  Text,
  TextInput,
  Textarea,
  Title,
  clsx,
} from "@mantine/core";
import cuid2 from "@paralleldrive/cuid2";
import { LegalDocumentType } from "@prisma/client";
import {
  json,
  redirect,
  unstable_composeUploadHandlers,
  unstable_createMemoryUploadHandler,
  unstable_parseMultipartFormData,
} from "@remix-run/node";
import {
  Form,
  useActionData,
  useLoaderData,
  useNavigation,
  useSearchParams,
} from "@remix-run/react";
import { ActionFunctionArgs } from "@remix-run/router";
import format from "date-fns/format/index.js";
import { useEffect, useState } from "react";
import { z } from "zod";

function createSchema(
  intent: Intent | null,
  constraints: {
    isEmailUnique?: (email: string) => Promise<boolean>;
  } = {},
) {
  return z.object({
    companyInformation: z.object({
      companyName: z.string().min(1),
      vatNumber: z.string().min(1),
      address: z.string().min(1),
      country: z.string().min(1),
      zipCode: z.string().min(1),
      contactPersonFirstName: z.string().min(1),
      contactPersonLastName: z.string().min(1),
      contactPersonEmail: z
        .string()
        .email()
        .superRefine((email, ctx) => {
          const isValidatingEmail =
            intent === null ||
            (intent.type === "validate" &&
              intent.payload.name === "companyInformation.contactPersonEmail");

          if (!isValidatingEmail) {
            ctx.addIssue({
              code: "custom",
              message: conformZodMessage.VALIDATION_SKIPPED,
            });
            return;
          }

          if (typeof constraints.isEmailUnique !== "function") {
            ctx.addIssue({
              code: "custom",
              message: conformZodMessage.VALIDATION_UNDEFINED,
              fatal: true,
            });
            return;
          }

          return constraints.isEmailUnique(email).then((isUnique) => {
            if (!isUnique) {
              ctx.addIssue({
                code: "custom",
                message: "Email is already taken",
              });
            }
          });
        }),
      contactPersonPhone: z.string().min(1),
    }),
    briefInformation: z
      .object({
        title: z.string().min(1),
        values: z.string().min(1),
        subcategory1: z.string().min(1),
        subcategory2: z.string().optional(),
        purpose: z.string().min(1),
        audience: z.string().min(1),
        marketInformation: z.string().min(1),
        functionality: z.string().min(1),
        materials: z.string().min(1),
        manufacturingRequirements: z.string().optional(),
        regulatoryStandards: z.string().optional(),
        retailPrice: z.enum([
          "ENTRY_LEVEL",
          "MEDIUM_LEVEL",
          "PREMIUM_LEVEL",
          "LUXURY_LEVEL",
        ]),
        retailPriceDescription: z.string().optional(),
        distributionChannel: z.enum(["B2C", "B2B", "B2C_AND_B2B"]),
        distributionChannelDescription: z.string().optional(),
        designStyle: z.string().optional(),
        sustainablePrinciples: z.string().optional(),
        packagingIncluded: z.preprocess((v) => v === "true", z.boolean()),
        packagingDescription: z.string().optional(),
        designRegisteringAssistance: z.preprocess(
          (v) => v === "true",
          z.boolean(),
        ),
        awardsAssistance: z.preprocess((v) => v === "true", z.boolean()),
        relationsAssistance: z.preprocess((v) => v === "true", z.boolean()),
        publicRelationsAssistance: z.preprocess(
          (v) => v === "true",
          z.boolean(),
        ),
        marketingAssistance: z.preprocess((v) => v === "true", z.boolean()),
        files: z.array(
          z.any().refine((val) => {
            if (typeof window === "undefined") return typeof val === "string";

            return val instanceof File;
          }),
        ),
      })
      .superRefine((data, ctx) => {
        if (
          data.subcategory1 &&
          data.subcategory1 in DESIGN_SUBCATEGORIES2 &&
          DESIGN_SUBCATEGORIES2[data.subcategory1].length > 0 &&
          !data.subcategory2
        ) {
          ctx.addIssue({
            code: "custom",
            path: ["subcategory2"],
            message: "Subcategory 2 is required",
          });
        }
      }),
    financialAndLegalInformation: z
      .object({
        pricing: z.preprocess(
          (v) => (Array.isArray(v) ? v.join(", ") : v),
          z.string().min(1),
        ),
        privacyPolicyAccepted: z.any().refine((v) => v === "on", {
          message: "Policies must be accepted",
        }),
      })
      .default({
        pricing: "",
        privacyPolicyAccepted: false,
      }),
  });
}

export default function GetQuotePage() {
  const loaderData = useLoaderData<typeof loader>();
  const lastResult = useActionData<typeof action>();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const isSuccess = searchParams.get("success") === "true";

  const [form, fields] = useForm({
    lastResult: lastResult?.submission,
    onValidate(context) {
      const result = parseWithZod(context.formData, { schema: createSchema });

      if (result.status !== "error" || !result.error) {
        return result;
      }

      const errorKeys = Object.keys(result.error);

      if (errorKeys.some((key) => key.startsWith("companyInformation"))) {
        setActiveStep(0);
      } else if (errorKeys.some((key) => key.startsWith("briefInformation"))) {
        setActiveStep(1);
      } else if (
        errorKeys.some((key) => key.startsWith("financialAndLegalInformation"))
      ) {
        setActiveStep(2);
      }

      return result;
    },
  });

  const [activeStep, setActiveStep] = useState(0);
  const [selectedSubcategory1, setSelectedSubcategory1] = useState<
    string | null
  >(null);
  const [policyOpen, setPolicyOpen] = useState(false);

  const companyFieldset = fields.companyInformation.getFieldset();
  const briefFieldset = fields.briefInformation.getFieldset();
  const financialAndLegalFieldset =
    fields.financialAndLegalInformation.getFieldset();
  const subcategory2Control = useInputControl(briefFieldset.subcategory2);
  const policyControl = useInputControl(
    financialAndLegalFieldset.privacyPolicyAccepted,
  );

  useEffect(() => {
    if (!lastResult?.submission) return;
    if (
      lastResult.submission.status !== "error" ||
      !lastResult.submission.error
    )
      return;

    const errorKeys = Object.keys(lastResult.submission.error);

    if (errorKeys.some((key) => key.startsWith("companyInformation"))) {
      setActiveStep(0);
    } else if (errorKeys.some((key) => key.startsWith("briefInformation"))) {
      setActiveStep(1);
    } else if (
      errorKeys.some((key) => key.startsWith("financialAndLegalInformation"))
    ) {
      setActiveStep(2);
    }
  }, [lastResult]);

  return (
    <PublicLayout>
      <div className="container mx-auto mt-12 max-w-[1108px]">
        <h4 className="text-center text-lg font-thin">
          Thinking about you next breakthrough product and would like to work
          with us?
          <br />
          Fill out the information below and the brief and get a custom
          quotation within 48 hours.
        </h4>
        <div className="flex mt-12">
          <div className="flex flex-col items-center">
            <div className="w-6 h-6 bg-black text-white flex items-center justify-center rounded-full">
              1
            </div>
            <span className="text-sm mt-2">Company information</span>
          </div>
          <div className="flex grow h-px bg-gray-300 mt-3" />
          <div
            className={clsx(
              "flex flex-col items-center",
              activeStep > 0 || isSuccess ? "opacity-100" : "opacity-30",
            )}
          >
            <div
              className={clsx(
                "w-6 h-6 bg-black text-white flex items-center justify-center rounded-full",
              )}
            >
              2
            </div>
            <span className="text-sm mt-2">Fill out brief</span>
          </div>
          <div className="flex grow h-px bg-gray-300 mt-3" />
          <div
            className={clsx(
              "flex flex-col items-center",
              activeStep > 1 || isSuccess ? "opacity-100" : "opacity-30",
            )}
          >
            <div className="w-6 h-6 bg-black text-white flex items-center justify-center rounded-full">
              3
            </div>
            <span className="text-sm mt-2">Financial and legal</span>
          </div>
          <div className="flex grow h-px bg-gray-300 mt-3" />
          <div
            className={clsx(
              "flex flex-col items-center",
              activeStep > 2 || isSuccess ? "opacity-100" : "opacity-30",
            )}
          >
            <div className="w-6 h-6 bg-black text-white flex items-center justify-center rounded-full">
              4
            </div>
            <span className="text-sm mt-2">Submit quote</span>
          </div>
        </div>
        <div
          className={clsx(
            "mt-12 rounded-md border shadow bg-white p-12",
            !isSuccess && "hidden",
          )}
        >
          <h3 className="text-lg font-thin text-center">
            Submitted successfully
          </h3>
          <p className="text-center text-sm font-thin mt-8">
            Thank you for submitting your request for a quotation and your
            Design Brief, A Designers Trust staff will contact you with a
            customized offer based on your enquiry within the next 48 hours
            (about 2 days).
          </p>
          <p className="text-center text-sm font-thin mt-4">
            Please look out for an email from one of our staff on the email
            address you have provided. Please note that the email might end up
            in being caught in your Spam filter.
          </p>
        </div>
        <Form
          method="POST"
          encType="multipart/form-data"
          className={clsx(isSuccess && "hidden")}
          {...getFormProps(form)}
        >
          <div className="mt-12 overflow-visible rounded-md border shadow bg-white p-4">
            <div className={clsx(activeStep !== 0 && "hidden")}>
              <>
                <Title order={4} weight={400}>
                  Company information
                </Title>
                <div className="grid grid-cols-12 gap-6 mt-4">
                  <div className="md:col-span-6">
                    <TextInput
                      label="Company name"
                      required
                      {...getInputProps(companyFieldset.companyName, {
                        type: "text",
                      })}
                      error={companyFieldset.companyName.errors?.[0]}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <TextInput
                      label="Company VAT number"
                      required
                      {...getInputProps(companyFieldset.vatNumber, {
                        type: "text",
                      })}
                      error={companyFieldset.vatNumber.errors?.[0]}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <TextInput
                      label="Address"
                      required
                      {...getInputProps(companyFieldset.address, {
                        type: "text",
                      })}
                      error={companyFieldset.address.errors?.[0]}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <TextInput
                      label="Zip code"
                      required
                      {...getInputProps(companyFieldset.zipCode, {
                        type: "text",
                      })}
                      error={companyFieldset.zipCode.errors?.[0]}
                    />
                  </div>
                  <div className="md:col-span-3">
                    <Select
                      label="Country"
                      required
                      data={Object.entries(loaderData.countries).map(
                        ([code, name]) => ({
                          value: code,
                          label: name,
                        }),
                      )}
                      {...getSelectProps(companyFieldset.country)}
                      error={companyFieldset.country.errors?.[0]}
                    />
                  </div>
                </div>
                <Title order={4} weight={400} className="pt-8">
                  Contact person information
                </Title>
                <div className="grid grid-cols-12 gap-6 mt-4">
                  <div className="md:col-span-6">
                    <TextInput
                      label="Contact person first name"
                      required
                      {...getInputProps(
                        companyFieldset.contactPersonFirstName,
                        {
                          type: "text",
                        },
                      )}
                      error={companyFieldset.contactPersonFirstName.errors?.[0]}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <TextInput
                      label="Contact person last name"
                      required
                      {...getInputProps(companyFieldset.contactPersonLastName, {
                        type: "text",
                      })}
                      error={companyFieldset.contactPersonLastName.errors?.[0]}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <TextInput
                      label="Contact person email"
                      required
                      {...getInputProps(companyFieldset.contactPersonEmail, {
                        type: "email",
                      })}
                      error={companyFieldset.contactPersonEmail.errors?.[0]}
                    />
                  </div>
                  <div className="md:col-span-6">
                    <TextInput
                      label="Contact person phone number"
                      required
                      {...getInputProps(companyFieldset.contactPersonPhone, {
                        type: "text",
                      })}
                      error={companyFieldset.contactPersonPhone.errors?.[0]}
                    />
                  </div>
                </div>
                <div className="mt-8 flex justify-end gap-4">
                  <Button variant="default">Cancel</Button>
                  <Button onClick={() => setActiveStep(1)}>Next step</Button>
                </div>
              </>
            </div>
            <div className={clsx(activeStep !== 1 && "hidden")}>
              <Title order={4} weight={400}>
                Section 1: General information
              </Title>
              <div className="grid grid-cols-12 gap-6 mt-4">
                <div className="col-span-12">
                  <TextInput
                    required
                    label="Design Pitch title"
                    {...getInputProps(briefFieldset.title, {
                      type: "text",
                    })}
                    error={briefFieldset.title.errors?.[0]}
                  />
                </div>
                <div className="col-span-12">
                  <Textarea
                    required
                    label="Brand values and Brand promise"
                    {...getTextareaProps(briefFieldset.values)}
                    error={briefFieldset.values.errors?.[0]}
                    description="Please describe/list the core values of your Brand, and what your brand promise is towards your customers"
                  />
                </div>
                <div className="col-span-6">
                  <Select
                    label="Product subcategory 1"
                    withAsterisk
                    required
                    data={Object.values(DESIGN_SUBCATEGORIES1).flatMap((v) =>
                      v.map((v2) => ({
                        value: v2.name,
                        label: v2.name,
                      })),
                    )}
                    {...getSelectProps(briefFieldset.subcategory1)}
                    onChange={(v) => {
                      setSelectedSubcategory1(v);
                      subcategory2Control.change("");
                    }}
                    error={briefFieldset.subcategory1.errors?.[0]}
                  />
                </div>
                <div className="col-span-6">
                  <Select
                    label="Product subcategory 2"
                    withAsterisk
                    required
                    disabled={!selectedSubcategory1}
                    value={subcategory2Control.value}
                    onChange={subcategory2Control.change}
                    onBlur={subcategory2Control.blur}
                    name="subcategory2"
                    error={briefFieldset.subcategory2.errors?.[0]}
                    data={
                      selectedSubcategory1
                        ? DESIGN_SUBCATEGORIES2[selectedSubcategory1]
                          ? DESIGN_SUBCATEGORIES2[selectedSubcategory1].map(
                              (v) => ({
                                value: v.name,
                                label: v.name,
                              }),
                            )
                          : []
                        : []
                    }
                  />
                </div>
              </div>
              <Title order={4} weight={400} className="pt-8">
                Section 2: Product information
              </Title>
              <div className="grid grid-cols-12 gap-6 mt-4">
                <div className="col-span-12">
                  <Textarea
                    required
                    label="Overall purpose of the project"
                    description="A description of the aims, and what your Brand would like to achieve with this project"
                    {...getTextareaProps(briefFieldset.purpose)}
                    error={briefFieldset.purpose.errors?.[0]}
                  />
                </div>
                <div className="col-span-12">
                  <Textarea
                    required
                    label="Target Audience"
                    description="Please describe the desired target group by age, gender, other characteristics and geographical region"
                    {...getTextareaProps(briefFieldset.audience)}
                    error={briefFieldset.audience.errors?.[0]}
                  />
                </div>
                <div className="col-span-12">
                  <Textarea
                    required
                    label="Market information and competitors"
                    description="Please highlight direct and indirect competitors within the market, as well as any relevant products in functionality and appearance"
                    {...getTextareaProps(briefFieldset.marketInformation)}
                    error={briefFieldset.marketInformation.errors?.[0]}
                  />
                </div>
                <div className="col-span-12">
                  <Textarea
                    required
                    label="Functionality"
                    description="Please describe how the product should work including functional requirements, ergonomic requirements, novelty factors and if there are any specific features that will make the product stand out"
                    {...getTextareaProps(briefFieldset.functionality)}
                    error={briefFieldset.functionality.errors?.[0]}
                  />
                </div>
                <div className="col-span-12">
                  <Textarea
                    required
                    label="Materials"
                    description="Please describe material requirements and rationale in the choice"
                    {...getTextareaProps(briefFieldset.materials)}
                    error={briefFieldset.materials.errors?.[0]}
                  />
                </div>
                <div className="col-span-12">
                  <Textarea
                    label="Manufacturing requirements (Optional)"
                    description="Please describe specific requirements that the designers need to keep in mind, that can have an impact on manufacturing ex: “injection molding”, “CNC machined” etc."
                    {...getTextareaProps(
                      briefFieldset.manufacturingRequirements,
                    )}
                    error={briefFieldset.manufacturingRequirements.errors?.[0]}
                  />
                </div>
                <div className="col-span-12">
                  <Textarea
                    label="Regulatory Standards/Requirements (Optional)"
                    description="Please describe if there are any specific regulatory requirements that may affect or drive the design concept – Additional information can be uploaded under point 2.15"
                    {...getTextareaProps(briefFieldset.regulatoryStandards)}
                    error={briefFieldset.regulatoryStandards.errors?.[0]}
                  />
                </div>
                <div className="col-span-12">
                  <p className="text-sm font-semibold">
                    Desired retail price <span className="text-red-500">*</span>
                  </p>
                  <p className="text-xs text-[#868e96] font-thin">
                    Please select and further describe desired retail price and
                    the rationale for supporting the product price
                  </p>
                  <Radio.Group
                    name={briefFieldset.retailPrice.name}
                    className="my-4"
                    defaultValue="ENTRY_LEVEL"
                  >
                    <div className="flex items-center gap-4">
                      <Radio value="ENTRY_LEVEL" label="Entry level" />
                      <Radio value="MEDIUM_LEVEL" label="Medium level" />
                      <Radio value="PREMIUM_LEVEL" label="Premium level" />
                      <Radio value="LUXURY_LEVEL" label="Luxury level" />
                    </div>
                  </Radio.Group>
                  <Textarea
                    {...getTextareaProps(briefFieldset.retailPriceDescription)}
                    error={briefFieldset.retailPriceDescription.errors?.[0]}
                  />
                </div>
                <div className="col-span-12">
                  <p className="text-sm font-semibold">
                    Planned distribution channels for the product (Optional)
                  </p>
                  <p className="text-xs text-[#868e96] font-thin">
                    Please select and further describe desired retail price and
                    the rationale for supporting the product price
                  </p>
                  <Radio.Group
                    name={briefFieldset.distributionChannel.name}
                    className="my-4"
                    defaultValue="B2C_AND_B2B"
                  >
                    <div className="flex items-center gap-4">
                      <Radio
                        value="B2C"
                        label="B2C (Sold directly to customers)"
                      />
                      <Radio
                        value="B2B"
                        label="B2B (Sold directly to businesses)"
                      />
                      <Radio value="B2C_AND_B2B" label="Both B2C & B2B" />
                    </div>
                  </Radio.Group>
                  <Textarea
                    {...getTextareaProps(
                      briefFieldset.distributionChannelDescription,
                    )}
                    error={
                      briefFieldset.distributionChannelDescription.errors?.[0]
                    }
                  />
                </div>
                <div className="col-span-12">
                  <Textarea
                    label="Design style and/or inspiration (Optional)"
                    description="Describe the overall design style and/or what inspired the project"
                    {...getTextareaProps(briefFieldset.designStyle)}
                    error={briefFieldset.designStyle.errors?.[0]}
                  />
                </div>
                <div className="col-span-12">
                  <Textarea
                    label="Sustainable principles (Optional)"
                    description="Please describe if there are any specific requirements in how to make the product more sustainable"
                    {...getTextareaProps(briefFieldset.sustainablePrinciples)}
                    error={briefFieldset.sustainablePrinciples.errors?.[0]}
                  />
                </div>
              </div>
              <Title order={4} weight={400} className="pt-8">
                Section 3: Additional services (Optional)
              </Title>
              <div className="grid grid-cols-12 gap-6 mt-4">
                <div className="col-span-12">
                  <p className="text-sm font-semibold">
                    Is packaging design for the product to be included?
                    (Optional)
                  </p>
                  <Radio.Group
                    name={briefFieldset.packagingIncluded.name}
                    defaultValue="false"
                    className="my-4"
                  >
                    <div className="flex items-center gap-4">
                      <Radio value="true" label="Yes" />
                      <Radio value="false" label="No" />
                    </div>
                  </Radio.Group>
                  <Textarea
                    {...getTextareaProps(briefFieldset.packagingDescription)}
                    error={briefFieldset.packagingDescription.errors?.[0]}
                  />
                </div>
                <div className="col-span-12">
                  <p className="text-sm font-semibold">
                    Do you require assistance with registering the design?
                    (optional)
                  </p>
                  <p className="text-xs text-[#868e96] font-thin">
                    (Assistance provided by Designers Trust)
                  </p>
                  <Radio.Group
                    name={briefFieldset.designRegisteringAssistance.name}
                    defaultValue="false"
                    className="my-4"
                  >
                    <div className="flex items-center gap-4">
                      <Radio value="true" label="Yes" />
                      <Radio value="false" label="No" />
                    </div>
                  </Radio.Group>
                </div>
                <div className="col-span-12">
                  <p className="text-sm font-semibold">
                    Do you require assistance with registration of final product
                    for Design awards? (Optional)
                  </p>
                  <p className="text-xs text-[#868e96] font-thin">
                    (Assistance provided by Designers Trust)
                  </p>
                  <Radio.Group
                    name={briefFieldset.awardsAssistance.name}
                    defaultValue="false"
                    className="my-4"
                  >
                    <div className="flex items-center gap-4">
                      <Radio value="true" label="Yes" />
                      <Radio value="false" label="No" />
                    </div>
                  </Radio.Group>
                </div>
                <div className="col-span-12">
                  <p className="text-sm font-semibold">
                    Do you require assistance with public relations, marketing
                    and reference rights?
                  </p>
                  <p className="text-xs text-[#868e96] font-thin">
                    (Assistance provided by Designers Trust)
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Public relations:</span>
                    <Radio.Group
                      name={briefFieldset.publicRelationsAssistance.name}
                      defaultValue="false"
                      className="my-4"
                    >
                      <div className="flex items-center gap-4">
                        <Radio value="true" label="Yes" />
                        <Radio value="false" label="No" />
                      </div>
                    </Radio.Group>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">Marketing:</span>
                    <Radio.Group
                      name={briefFieldset.marketingAssistance.name}
                      defaultValue="false"
                      className="my-4"
                    >
                      <div className="flex items-center gap-4">
                        <Radio value="true" label="Yes" />
                        <Radio value="false" label="No" />
                      </div>
                    </Radio.Group>
                  </div>
                </div>
              </div>
              <Title order={4} weight={400} className="pt-8">
                Section 4: Upload of additional materials
              </Title>
              <p className="text-xs font-thin my-4">
                Feel free to add any further information that could guide the
                Designer e.g. Identity Artwork (vector files preferred), Product
                Design Manuel, Mood board, Background research etc.
              </p>
              <FileInput
                label="Upload additional materials"
                description="Files, documents, animation files, etc. Up to 10 different files, max 5MB each"
                multiple
                {...getInputProps(briefFieldset.files, {
                  type: "file",
                })}
              />
              <div className="mt-8 flex justify-end gap-4">
                <Button variant="default" onClick={() => setActiveStep(0)}>
                  Previous step
                </Button>
                <Button onClick={() => setActiveStep(2)}>Next step</Button>
              </div>
            </div>
            <div className={clsx(activeStep !== 2 && "hidden")}>
              <Title order={4} weight={400}>
                Desired financial agreement option
              </Title>
              <p className="font-thin text-sm mt-8">
                Designers Trust aims to offer premium services at competitive
                pricing, therefore for design briefs we operate on the following
                payment options presented below: Please select one of the
                options from above that you would prefer to follow if you decide
                to proceed further with your Design brief after your quotation.
                This will help Designers Trust create a more accurate and
                customized quotation for you.
              </p>
              <Checkbox
                className="mt-8"
                name={financialAndLegalFieldset.pricing.name}
                label="Design Fee + Royalty (starting from EUR 5,000.00 design fee + Royalty agreement)"
                value="Design Fee + Royalty (starting from EUR 5,000.00 design fee + Royalty agreement)"
              />
              <Checkbox
                className="mt-4"
                name={financialAndLegalFieldset.pricing.name}
                label="Full Design fee (starting at EUR 35,000.00 and does not include a royalty agreement)"
                value="Full Design fee (starting at EUR 35,000.00 and does not include a royalty agreement)"
              />
              {(financialAndLegalFieldset.pricing.errors?.[0] ||
                financialAndLegalFieldset.pricing.errors?.[0]) && (
                <p className="text-red-500 text-sm mt-2">
                  Please select a pricing option
                </p>
              )}
              <p className="text-sm mt-8 font-thin italic">Disclaimer</p>
              <p className="text-sm font-thin mt-4 italic">
                Upon uploading the "design brief", the Brand will receive an
                offer from Designers Trust. Upon accepting the offer, the brand
                will be invoiced for the first instalment at the commencement of
                Phase I (ex. VAT). 6 (six) weeks after the start of the Design
                Brief, The Designer and Designers Trust will present the Brand
                with 3 concepts based on the design brief requirement. After the
                presentation, the Brand will have the option to select one or
                more of the concepts and allow the designer to develop them into
                a production-ready design.
              </p>
              <Checkbox
                name="privacyPolicy"
                checked={policyControl.value === "on"}
                onBlur={policyControl.blur}
                onClick={(e) => {
                  if (policyControl.value === "on") {
                    policyControl.change("");
                  } else {
                    setPolicyOpen(true);
                  }
                }}
                onChange={() => {}}
                mt="md"
                error={
                  financialAndLegalFieldset.privacyPolicyAccepted.errors?.[0]
                }
                label={
                  <>
                    I have read and acknowledged the{" "}
                    <Text component="span" weight={600}>
                      Privacy Policy
                    </Text>{" "}
                    and the{" "}
                    <Text component="span" weight={600}>
                      Terms and conditions
                    </Text>
                  </>
                }
              />

              {policyOpen && (
                <PrivacyTermsDialog
                  onClose={() => setPolicyOpen(false)}
                  onAccept={() => {
                    policyControl.change("on");

                    setPolicyOpen(false);
                  }}
                />
              )}
              <div className="mt-8 flex justify-end gap-4">
                <Button variant="default" onClick={() => setActiveStep(1)}>
                  Previous step
                </Button>
                <Button
                  type="submit"
                  loading={navigation.state === "submitting"}
                >
                  Submit
                </Button>
              </div>
            </div>
          </div>
        </Form>
      </div>
    </PublicLayout>
  );
}

export async function loader() {
  const privacyPolicyRenderer = new LegalTemplateRenderer(
    LegalDocumentType.PRIVACY_POLICY,
    1,
  );
  const termsAndConditionsRenderer = new LegalTemplateRenderer(
    LegalDocumentType.TERMS_AND_CONDITIONS,
    1,
  );

  const [privacyPolicy, termsAndConditions] = await Promise.all([
    privacyPolicyRenderer.render({
      currentDate: format(new Date(), "dd/MM/yyyy"),
    }),
    termsAndConditionsRenderer.render({
      currentDate: format(new Date(), "dd/MM/yyyy"),
    }),
  ]);

  return json({
    countries: getCountriesCodeAndName(),
    privacyPolicy: privacyPolicy.unwrapOr("Failed to load privacy policy"),
    termsAndConditions: termsAndConditions.unwrapOr(
      "Failed to load terms and conditions",
    ),
  });
}

export async function action({ request }: ActionFunctionArgs) {
  const pitchId = cuid2.createId();
  const userId = cuid2.createId();

  const fileNames: string[] = [];
  const uploadHandler = unstable_composeUploadHandlers(
    createS3UploadHandler({
      maxPartSize: 50_000_000,
      filter: ({ filename }) => {
        if (!filename) return false;

        fileNames.push(filename);
        return true;
      },
      prefix: `pitches/${pitchId}/files`,
    }),
    unstable_createMemoryUploadHandler({
      filter: ({ filename }) => {
        return !filename;
      },
    }),
  );
  const formData = await unstable_parseMultipartFormData(
    request,
    uploadHandler,
  );

  const submission = await parseWithZod(formData, {
    schema: (intent) =>
      createSchema(intent, {
        async isEmailUnique(email) {
          return !(await prisma.user.findUnique({
            where: {
              email: email,
            },
          }));
        },
      }),
    async: true,
  });

  if (submission.status !== "success") {
    return json(
      {
        submission: submission.reply(),
      },
      {
        status: submission.status === "error" ? 422 : 200,
      },
    );
  }

  const [termsOfService, privacyPolicy] = await Promise.all([
    prisma.legalDocument.findFirst({
      where: {
        type: LegalDocumentType.TERMS_AND_CONDITIONS,
      },
      orderBy: {
        revision: "desc",
      },
    }),
    prisma.legalDocument.findFirst({
      where: {
        type: LegalDocumentType.PRIVACY_POLICY,
      },
      orderBy: {
        revision: "desc",
      },
    }),
  ]);

  await prisma.user.create({
    data: {
      id: userId,
      isShadowUser: true,
      email: submission.value.companyInformation.contactPersonEmail,
      activeRole: {
        connect: {
          type: "BRAND",
        },
      },
      roles: {
        connect: {
          type: "BRAND",
        },
      },
      legalDocuments: {
        createMany: {
          data: [
            {
              documentId: termsOfService.id,
              signedAt: new Date(),
              signedAs: "BRAND",
            },
            {
              documentId: privacyPolicy.id,
              signedAt: new Date(),
              signedAs: "BRAND",
            },
          ],
        },
      },
      brandProfile: {
        create: {
          companyName: submission.value.companyInformation.companyName,
          followUp: false,
          vatNumber: submission.value.companyInformation.vatNumber,
          mainFirstName:
            submission.value.companyInformation.contactPersonFirstName,
          mainLastName:
            submission.value.companyInformation.contactPersonLastName,
          mainEmail: submission.value.companyInformation.contactPersonEmail,
          mainPhone: submission.value.companyInformation.contactPersonPhone,
          country: {
            connect: {
              alpha2: submission.value.companyInformation.country,
            },
          },
        },
      },
      pitches: {
        create: {
          desiredRetailPriceOption:
            submission.value.briefInformation.retailPrice,
          distributionChannelOption:
            submission.value.briefInformation.distributionChannel,
          state: "DRAFT",
          assistanceRegisteringAwards:
            submission.value.briefInformation.awardsAssistance,
          assistanceRegisteringDesign:
            submission.value.briefInformation.designRegisteringAssistance,
          assistanceRegisteringMarketing:
            submission.value.briefInformation.marketingAssistance,
          assistanceRegisteringRelations:
            submission.value.briefInformation.relationsAssistance,
          brandPaymentOption:
            submission.value.financialAndLegalInformation.pricing,
          brandValues: submission.value.briefInformation.values,
          designStyle: submission.value.briefInformation.designStyle,
          desiredRetailPriceDescription:
            submission.value.briefInformation.retailPriceDescription,
          distributionChannelDescription:
            submission.value.briefInformation.distributionChannelDescription,
          functionality: submission.value.briefInformation.functionality,
          manufacturingRequirements:
            submission.value.briefInformation.manufacturingRequirements,
          marketInformation:
            submission.value.briefInformation.marketInformation,
          materials: submission.value.briefInformation.materials,
          packagingDescription:
            submission.value.briefInformation.packagingDescription,
          packagingIncluded:
            submission.value.briefInformation.packagingIncluded,
          purpose: submission.value.briefInformation.purpose,
          regulatoryRequirements:
            submission.value.briefInformation.regulatoryStandards,
          subcategory1: submission.value.briefInformation.subcategory1,
          subcategory2: submission.value.briefInformation.subcategory2,
          sustainablePrinciples:
            submission.value.briefInformation.sustainablePrinciples,
          targetAudience: submission.value.briefInformation.audience,
          title: submission.value.briefInformation.title,
          files: {
            createMany: {
              data: fileNames.map((filename, index) => ({
                userId,
                location: submission.value.briefInformation.files[index],
                uploader: "BRAND",
                name: filename,
              })),
            },
          },
        },
      },
    },
  });

  return redirect("/quote?success=true");
}
